import { FC, useMemo } from "react";
import type { BaseMessage, FileMessage } from "@sendbird/chat/message";
import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import ChatMediaViewer, { iChatMediaItem } from "./ChatMediaViewer";

const isMediaMessage = (message: BaseMessage): message is FileMessage => {
	if (!message.isFileMessage()) return false;
	const { type, url } = message as FileMessage;
	return Boolean(url) && (type?.startsWith("image/") || type?.startsWith("video/"));
};

interface iChatMediaViewerHostProps {
	openedMessageId: number | null;
	onClose: () => void;
}

/** Opens the chat's photos and videos as one gallery, starting at the one that was clicked. */
const ChatMediaViewerHost: FC<iChatMediaViewerHostProps> = ({ openedMessageId, onClose }) => {
	const { messages } = useGroupChannelContext();

	// Taken once per opening, so messages arriving while the viewer is open do not shift it.
	const items = useMemo<iChatMediaItem[]>(
		() =>
			openedMessageId === null
				? []
				: messages.filter(isMediaMessage).map((message) => ({
						key: String(message.messageId),
						name: message.name,
						url: message.url,
						isVideo: message.type.startsWith("video/"),
					})),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[openedMessageId],
	);

	const index = items.findIndex((item) => item.key === String(openedMessageId));
	if (index < 0) return null;

	return <ChatMediaViewer key={openedMessageId} items={items} index={index} onClose={onClose} />;
};

export default ChatMediaViewerHost;
